import * as React from 'react';
import { H2, H3 } from 'src/components/Elements/Elements';
import { Flex, FlexDirection } from 'src/components/Flex/Flex';
import { FlexItem, TextPosition } from 'src/components/FlexItem/FlexItem';
import {
  Color,
  FlexPosition,
  ResponsiveSize,
  ErrorMessages,
  FontAwesomeFileIcons,
  FileTypeColors
} from 'src/components/Globals/Globals';
import { MultiSelectFilter } from 'src/components/MultiSelectFilter/MultiSelectFilter';
import { MultiSelectInput } from 'src/components/MultiSelectInput/MultiSelectInput';
import { AppConsumer } from 'src/contexts/Context';
import { ImageCard } from '../../components/ImageCard/ImageCard';
import { Container, WideContainer } from 'src/components/Container/Container';
import { Loader } from '../../components/Loader/Loader';
import { OfferingTable } from 'src/components/OfferingTable/OfferingTable';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as ReactTooltip from 'react-tooltip';
import { Link } from 'react-router-dom';

const FilterWrapper = styled(Flex)`
  @media (max-width: ${ResponsiveSize.laptop}) {
    flex-direction: row;
  }
  @media (max-width: ${ResponsiveSize.tablet}) {
    flex-direction: column;
  }
`;

const InfoIcon = styled(FontAwesomeIcon)`
  font-size: 1rem;
  text-align: right;
  display: flex;
  align-items: center;
`;

const InfoIconWrapper = styled.div`
  display: inline-block;
  margin: auto 7px;
  position: relative;
  display: inline-block;
`;

const ReportLinkContainer = styled.div`
  display: inline-block;
  margin-left: 6px;
`;

const ExternalLink = styled.a`
  padding: 16px;
  background: ${Color.lighterBlue};
  text-decoration: none;
  color: black;
  font-weight: 600;
  &:hover {
    background: ${Color.lightBlue};
  }
`;

// Search page, aka the front page of the site. Includes search filters,
// search results and most popular and latest solutions.
export class Search extends React.Component {
  public render() {
    return (
      <AppConsumer>
        {({ state, actions, apiActions, offeringTypeActions }) => (
          <Flex direction={FlexDirection.column} style={{ flexGrow: 1 }}>
            <WideContainer style={{ marginBottom: 20 }}>
              <Flex
                alignHorizontal={FlexPosition.center}
                tileAfter={ResponsiveSize.laptop}
                baseMargin={true}
              >
                <FlexItem>
                  <FilterWrapper direction={FlexDirection.column} margin="0px 20px">
                    <div>
                      <Flex alignHorizontal={FlexPosition.left}>
                        <H3>Offering type</H3>
                        <InfoIconWrapper data-tip="Filter solutions by offering type.<br/> See Value to customer submenu by selecting Upgrade solutions.">
                          <InfoIcon icon="info-circle" />
                        </InfoIconWrapper>
                      </Flex>
                      <MultiSelectFilter
                        options={state.offeringTypes}
                        filterFunction={actions.filterSolutions}
                      />
                    </div>

                    {offeringTypeActions.showValueToCustomer(state.offeringTypes) && (
                      <div>
                        <Flex alignHorizontal={FlexPosition.left}>
                          <H3>Value to customer</H3>
                        </Flex>
                        <MultiSelectFilter
                          options={state.valueToCustomerTypes}
                          filterFunction={actions.filterSolutions}
                          selectedColor={Color.grey200}
                        />
                      </div>
                    )}
                    <Flex direction={FlexDirection.column}>
                      <div>
                        <Flex alignHorizontal={FlexPosition.left}>
                          <H3>Product</H3>
                          <InfoIconWrapper data-tip="Filter solutions by product">
                            <InfoIcon icon="info-circle" />
                          </InfoIconWrapper>
                        </Flex>

                        <MultiSelectInput
                          options={state.productOptions}
                          selectedOptions={state.selectedProductOptions}
                          addItems={actions.addProducts}
                        />
                      </div>
                      <div>
                        <Flex alignHorizontal={FlexPosition.left}>
                          <H3>Industry</H3>
                          <InfoIconWrapper data-tip="Filter solutions by industry.">
                            <InfoIcon icon="info-circle" />
                          </InfoIconWrapper>
                        </Flex>
                        <MultiSelectInput
                          options={state.industryOptions}
                          selectedOptions={state.selectedIndustryOptions}
                          addItems={actions.addIndustries}
                        />
                      </div>
                      <div>
                        <Flex alignHorizontal={FlexPosition.left}>
                          <H3>Business line</H3>
                          <InfoIconWrapper data-tip="Filter solutions by business line">
                            <InfoIcon icon="info-circle" />
                          </InfoIconWrapper>
                        </Flex>

                        <MultiSelectInput
                          options={state.businessLineOptions}
                          selectedOptions={state.selectedBusinessLineOptions}
                          addItems={actions.addBusinesses}
                        />
                      </div>
                    </Flex>
                  </FilterWrapper>
                </FlexItem>

                <Flex direction={FlexDirection.column} flexSize={3} margin="0px 20px">
                  {state.solutionsLoaded ? (
                    <OfferingTable />
                  ) : (
                    <div style={{ height: 400 }}>
                      <H3>Solutions</H3>
                      <Loader
                        text="Loading solutions..."
                        failed={state.solutionLoadError}
                        failedText={ErrorMessages.networkError}
                      />
                    </div>
                  )}
                </Flex>
              </Flex>
            </WideContainer>

            <FlexItem alignText={TextPosition.left}>
              <Flex direction={FlexDirection.column} style={{ maxWidth: '100%', minWidth: '100%' }}>
                <Container style={{ background: Color.lighterBlue }}>
                  <Flex
                    direction={FlexDirection.column}
                    alignHorizontal={FlexPosition.center}
                    tileAfter={ResponsiveSize.laptop}
                    baseMargin={true}
                  >
                    <H2 style={{ marginBottom: 0 }}>Latest Solutions</H2>
                    <Flex
                      direction={FlexDirection.row}
                      style={{ overflowX: 'auto', maxWidth: '100%' }}
                    >
                      {state.latestSolutions.map(solution => (
                        <ImageCard
                          cardType="solution"
                          key={solution.id}
                          solutionId={solution.id}
                          url={'offering/' + solution.id}
                          description={solution.solutionName}
                          businessLines={solution.businessLine}
                          imageHtml={solution.solutionImage}
                          parseSolutionImageHtml={apiActions.parseSolutionImageHtml}
                        />
                      ))}
                    </Flex>
                  </Flex>
                </Container>
                <Container>
                  <Flex
                    direction={FlexDirection.column}
                    alignHorizontal={FlexPosition.center}
                    tileAfter={ResponsiveSize.tablet}
                    baseMargin={true}
                  >
                    <H2 style={{ marginBottom: 0 }}>Latest Materials</H2>
                    <Flex
                      direction={FlexDirection.row}
                      style={{ overflowX: 'auto', maxWidth: '100%' }}
                    >
                      {state.latestMaterials.map(material => (
                        <ImageCard
                          cardType="file"
                          key={material.id}
                          solutionId={material.id}
                          description={material.title}
                          url={
                            material.relatedSolutionId
                              ? 'offering/' + material.relatedSolutionId
                              : ''
                          }
                          fileIcon={FontAwesomeFileIcons[material.fileType]}
                          iconColor={FileTypeColors[material.fileType]}
                          fileType={material.fileType}
                          fileId={material.contentDocumentId}
                          fetchFileThumbnailInBase64={apiActions.fetchFileThumbnailInBase64}
                        />
                      ))}
                    </Flex>
                  </Flex>
                </Container>
                <Container style={{ background: Color.lighterBlue }}>
                  <Flex
                    direction={FlexDirection.column}
                    alignHorizontal={FlexPosition.center}
                    tileAfter={ResponsiveSize.laptop}
                    baseMargin={true}
                  >
                    <Flex direction={FlexDirection.row}>
                      <H2 style={{ marginBottom: 0 }}>
                        Popular Solutions (
                        <ReportLinkContainer data-tip="See a more extensive report of solution and material views.">
                          <Link to="/report">
                            Full report
                            <FontAwesomeIcon
                              icon="clipboard-list"
                              style={{ marginLeft: 5, marginRight: 5 }}
                            />
                          </Link>
                        </ReportLinkContainer>
                        )
                      </H2>
                    </Flex>
                    <Flex
                      direction={FlexDirection.row}
                      style={{ overflowX: 'auto', maxWidth: '100%' }}
                    >
                      {state.mostPopularSolutions.map(
                        value =>
                          value.solution && (
                            <ImageCard
                              cardType="solution"
                              key={value.solution.id}
                              solutionId={value.solution.id}
                              url={'offering/' + value.solution.id}
                              description={value.solution.solutionName}
                              businessLines={value.solution.businessLine}
                              imageHtml={value.solution.solutionImage}
                              parseSolutionImageHtml={apiActions.parseSolutionImageHtml}
                            />
                          )
                      )}
                    </Flex>
                  </Flex>
                </Container>
                <Container>
                  <Flex
                    direction={FlexDirection.column}
                    alignHorizontal={FlexPosition.center}
                    tileAfter={ResponsiveSize.tablet}
                    baseMargin={true}
                  >
                    <H2 style={{ marginBottom: 0 }}>
                      Popular Materials (
                      <ReportLinkContainer data-tip="See a more extensive report of solution and material views.">
                        <Link to="/report">
                          Full report
                          <FontAwesomeIcon
                            icon="clipboard-list"
                            style={{ marginLeft: 5, marginRight: 5 }}
                          />
                        </Link>
                      </ReportLinkContainer>
                      )
                    </H2>
                    <Flex
                      direction={FlexDirection.row}
                      style={{ overflowX: 'auto', maxWidth: '100%' }}
                    >
                      {state.mostPopularMaterials.map(material => (
                        <ImageCard
                          cardType="file"
                          key={material.id}
                          solutionId={material.id}
                          description={material.title}
                          url={
                            material.relatedSolutionId
                              ? 'offering/' + material.relatedSolutionId
                              : ''
                          }
                          fileIcon={FontAwesomeFileIcons[material.fileType]}
                          iconColor={FileTypeColors[material.fileType]}
                          fileType={material.fileType}
                          fileId={material.contentDocumentId}
                          fetchFileThumbnailInBase64={apiActions.fetchFileThumbnailInBase64}
                        />
                      ))}
                    </Flex>
                  </Flex>
                  <Flex
                    direction={FlexDirection.column}
                    alignHorizontal={FlexPosition.center}
                    tileAfter={ResponsiveSize.tablet}
                    baseMargin={true}
                  >
                    <H2 style={{ marginBottom: 0 }}>Other tools</H2>
                    <Flex
                      direction={FlexDirection.row}
                      style={{ overflowX: 'auto', maxWidth: '100%', padding: '20px 0px' }}
                    >
                      <ExternalLink href="https://wartsila.secure.force.com/PSMT/ " target="_blank">
                        Offering Potential search <FontAwesomeIcon icon="arrow-right" />
                      </ExternalLink>
                    </Flex>
                  </Flex>
                </Container>
              </Flex>
            </FlexItem>
            <ReactTooltip multiline={true} />
          </Flex>
        )}
      </AppConsumer>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Search/Search.tsx